/**
 * Synchronously-readable cache of the signed-in user's Firebase ID token,
 * ported from TITAN_dev's src/services/firebase/idToken.ts.
 *
 * Most callers should just await getIdToken(). This cache exists for the one
 * place that cannot await: mapbox-gl's `transformRequest`, which has to return
 * headers synchronously for every tile request.
 */
import firebase from 'firebase/app';
import 'firebase/auth';

let cachedToken: string | null = null;

// Firebase ID tokens live an hour; refresh at 45 minutes so a tile request
// never races expiry. onIdTokenChanged also fires on the SDK's silent refresh.
const REFRESH_MS = 45 * 60 * 1000;

firebase.auth().onIdTokenChanged(async user => {
    cachedToken = user ? await user.getIdToken() : null;
});

setInterval(() => {
    const user = firebase.auth().currentUser;
    if (user) {
        user.getIdToken().then(t => (cachedToken = t)).catch(() => undefined);
    }
}, REFRESH_MS);

/** The current ID token, or null when signed out or not yet primed. */
export const peekIdToken = (): string | null => cachedToken;

/** Await a primed cache; resolves false if no token arrives in time. */
export async function waitForIdToken(timeoutMs = 10000): Promise<boolean> {
    if (cachedToken) {
        return true;
    }
    const user = firebase.auth().currentUser;
    if (!user) {
        return false;
    }
    try {
        cachedToken = await user.getIdToken();
        return true;
    } catch {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
            if (cachedToken) {
                return true;
            }
            await new Promise(r => setTimeout(r, 250));
        }
        return false;
    }
}

/**
 * mapbox-gl transformRequest hook: attach the ID token to the probe tile
 * endpoint and to nothing else -- it must never travel to Mapbox's own style,
 * sprite or font hosts.
 */
export function titanTransformRequest(url?: string): {url: string; headers?: Record<string, string>} {
    if (!url) {
        return {url: ''};
    }
    if (!url.includes('/v1/probe/tiles/')) {
        return {url};
    }
    const token = peekIdToken();
    return token ? {url, headers: {Authorization: `Bearer ${token}`}} : {url};
}

/** Authorization header for fetch(); throws when signed out. */
export async function authHeader(): Promise<Record<string, string>> {
    const user = firebase.auth().currentUser;
    if (!user) {
        throw new Error('Not signed in');
    }
    return {Authorization: `Bearer ${await user.getIdToken()}`};
}
